export interface StageDef {
  id: string;
  label: string;
}

export const ACCOUNTANT_STAGES: StageDef[] = [
  { id: 'intake', label: 'INTAKE' },
  { id: 'reconcile', label: 'RECONCILE' },
  { id: 'engines', label: 'ENGINES' },
  { id: 'anomalies', label: 'ANOMALIES' },
  { id: 'defense', label: 'DEFENSE' },
  { id: 'war-room', label: 'WAR ROOM' },
  { id: 'sign-off', label: 'SIGN-OFF' },
  { id: 'deliver', label: 'DELIVER' },
];

// Attorney spine (K3 taxonomy, 2026-09-01): the plaintiff-attorney legal workflow —
// understand → grade → elect/defend method → strategize settlement → produce sworn
// deliverables → deliberate → finalize. One lighter than the accountant (no data-prep
// pipeline; the attorney consumes results + provenance, never the plumbing). "Finalize"
// (not "Deliver") — the attorney cannot attest; attestation is the external CPA's act.
export const ATTORNEY_STAGES: StageDef[] = [
  { id: 'intake', label: 'INTAKE' },
  { id: 'analysis', label: 'ANALYSIS' },
  { id: 'methodology', label: 'METHODOLOGY' },
  { id: 'settlement', label: 'SETTLEMENT' },
  { id: 'deliverables', label: 'DELIVERABLES' },
  { id: 'war-room', label: 'WAR ROOM' },
  { id: 'finalize', label: 'FINALIZE' },
];

export const DEFAULT_STAGES: StageDef[] = [
  { id: 'intake', label: 'Intake' },
  { id: 'data', label: 'Data' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'deliverables', label: 'Deliverables' },
  { id: 'settlement', label: 'Settlement' },
];

// Adjuster spine — Analysis (4.1) → Position (4.4). "Position" (not "Finalize"):
// the adjuster acknowledges the carrier position (who/when); it does not attest, so
// "Finalize" would read as the CPA act. Further stages are 4.2+.
export const ADJUSTER_STAGES: StageDef[] = [
  { id: 'analysis', label: 'ANALYSIS' },
  // War Room sits between analysis and position: the deliberation informs the
  // carrier position. Leads with the ai:adjuster counterpart + the defense preview.
  { id: 'war-room', label: 'WAR ROOM' },
  { id: 'position', label: 'POSITION' },
];

// Data-Specialist spine (FL-5 5.3 + B1 wizard acquisition, 2026-09-05, K3-approved).
// Provenance order — source → parties → incident → money → wages → ship. Documents
// first (everything downstream derives from it); Client/Event/Transactions/Payroll are
// the acquired Wizard V2 steps 1–4; Handoff is the DS release gate. Validation is NOT a
// peer stage — its checks already exist as Transactions ViewModes (recon/anomalies), and
// its gate function folds into Handoff (K3 gate: one surface per concern). AI Review is a
// persistent right rail, not a stage.
export const DATA_SPECIALIST_STAGES: StageDef[] = [
  { id: 'documents', label: 'DOCUMENTS' },
  { id: 'client', label: 'CLIENT' },
  { id: 'event', label: 'EVENT' },
  { id: 'transactions', label: 'TRANSACTIONS' },
  { id: 'payroll', label: 'PAYROLL' },
  // War Room before the Handoff release gate: leads with BOTH DS counterparts
  // (Data Readiness + Doc Health) so the specialist sees where the room stands
  // on data before releasing to the accountant.
  { id: 'war-room', label: 'WAR ROOM' },
  { id: 'handoff', label: 'HANDOFF' },
];

export type WorkspaceRole = 'accountant' | 'attorney' | 'adjuster' | 'data_specialist' | 'default';

export function stagesForRole(role: WorkspaceRole): StageDef[] {
  if (role === 'accountant') return ACCOUNTANT_STAGES;
  if (role === 'attorney') return ATTORNEY_STAGES;
  if (role === 'adjuster') return ADJUSTER_STAGES;
  if (role === 'data_specialist') return DATA_SPECIALIST_STAGES;
  return DEFAULT_STAGES;
}

export function firstStageForRole(role: WorkspaceRole): string {
  return stagesForRole(role)[0]?.id ?? 'intake';
}

export function isValidStageForRole(role: WorkspaceRole, stage: string): boolean {
  return stagesForRole(role).some((s) => s.id === stage);
}

export function normalizeStageForRole(role: WorkspaceRole, stage?: string): string {
  if (stage && isValidStageForRole(role, stage)) return stage;
  return firstStageForRole(role);
}
